/**
 * Anzeige der Funktionen eines Mitarbeiters als Badges (Hauptfunktion + weitere).
 *
 * Verhalten:
 *  - Wenn nur `position` gesetzt: zeigt diese als einzelnes Badge / als Text
 *  - Wenn `position_list` mehrere Einträge enthält: zeigt jede als Badge,
 *    Hauptfunktion (position) wird zuerst und mit ★-Marker hervorgehoben
 *  - Wenn nichts gesetzt: zeigt "—"
 *
 * Props:
 *  - employee: { position, position_list }
 *  - variant: 'inline' (Standard, kompakt für Tabellen) | 'block' (für Cards/Detail)
 */
import React from 'react';
import { Badge } from '../../components/ui/badge';

export const PositionBadges = ({ employee, variant = 'inline', testIdPrefix }) => {
  const primary = (employee?.position || '').trim();
  const list = Array.isArray(employee?.position_list)
    ? employee.position_list.map((p) => (typeof p === 'string' ? p.trim() : '')).filter(Boolean)
    : [];

  // Liste OHNE Duplikate (case-insensitive), Hauptfunktion zuerst
  const ordered = [];
  const seen = new Set();
  const norm = (s) => s.toLocaleLowerCase('de-DE');
  if (primary) { ordered.push(primary); seen.add(norm(primary)); }
  list.forEach((p) => {
    const key = norm(p);
    if (!seen.has(key)) { ordered.push(p); seen.add(key); }
  });

  if (ordered.length === 0) return <span className="text-muted-foreground">—</span>;

  // Nur eine Funktion → schlichter Text (kein Badge-Overkill)
  if (ordered.length === 1) {
    return <span data-testid={testIdPrefix ? `${testIdPrefix}-position` : undefined}>{ordered[0]}</span>;
  }

  const gap = variant === 'block' ? 'gap-1.5' : 'gap-1';
  return (
    <div className={`flex flex-wrap items-center ${gap}`} data-testid={testIdPrefix ? `${testIdPrefix}-positions` : undefined}>
      {ordered.map((p, idx) => (
        <Badge
          key={p}
          variant={idx === 0 ? 'secondary' : 'outline'}
          className="text-xs font-normal py-0"
          title={idx === 0 ? 'Hauptfunktion' : 'Weitere Funktion'}
          data-testid={testIdPrefix ? `${testIdPrefix}-position-${idx}` : undefined}
        >
          {idx === 0 && <span className="text-amber-500 mr-0.5">★</span>}
          {p}
        </Badge>
      ))}
    </div>
  );
};

export default PositionBadges;
